// Common constants used across the application

// API Configuration
export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_URL || 'http://localhost:8000/api',
  TIMEOUT: 10000,
  RETRY_ATTEMPTS: 3,
} as const;

// Common HTTP status codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
} as const;

// Common date formats
export const DATE_FORMATS = {
  DISPLAY: 'dd/MM/yyyy',
  DISPLAY_WITH_TIME: 'dd/MM/yyyy HH:mm',
  ISO: 'yyyy-MM-dd',
  ISO_WITH_TIME: 'yyyy-MM-dd HH:mm:ss',
  READABLE: 'dd \'de\' MMMM \'de\' yyyy',
} as const;

// Common pagination settings
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
  MAX_PAGE_SIZE: 100,
} as const;

// Common breakpoints (matching TailwindCSS)
export const BREAKPOINTS = {
  SM: 640,
  MD: 768,
  LG: 1024,
  XL: 1280,
  '2XL': 1536,
} as const;

// Common file types
export const FILE_TYPES = {
  IMAGES: ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'],
  DOCUMENTS: ['.pdf', '.doc', '.docx', '.txt', '.rtf'],
  SPREADSHEETS: ['.xls', '.xlsx', '.csv'],
  PRESENTATIONS: ['.ppt', '.pptx'],
  ARCHIVES: ['.zip', '.rar', '.7z', '.tar', '.gz'],
} as const;

// Common file size limits (in bytes)
export const FILE_SIZE_LIMITS = {
  SMALL: 1024 * 1024, // 1MB
  MEDIUM: 5 * 1024 * 1024, // 5MB
  LARGE: 10 * 1024 * 1024, // 10MB
  EXTRA_LARGE: 50 * 1024 * 1024, // 50MB
} as const;

// Single source of truth for the max upload size accepted for escritura,
// escritura publicada/inscripta and escrituras adicionales. Must match
// backend `uploads.max_escritura_size_kb` (backend/config/uploads.php).
export const MAX_ESCRITURA_FILE_SIZE_MB = 512;
export const MAX_ESCRITURA_FILE_SIZE_BYTES = MAX_ESCRITURA_FILE_SIZE_MB * 1024 * 1024;

// Common validation rules
export const VALIDATION = {
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE_REGEX: /^[\+]?[0-9\s\-\(\)]+$/,
  PASSWORD_MIN_LENGTH: 8,
  USERNAME_MIN_LENGTH: 3,
  USERNAME_MAX_LENGTH: 50,
} as const;

// Common error messages
export const ERROR_MESSAGES = {
  GENERIC: 'Ha ocurrido un error inesperado',
  NETWORK: 'Error de conexión. Verifique su conexión a internet',
  VALIDATION: 'Por favor, corrija los errores en el formulario',
  REQUIRED_FIELD: 'Este campo es requerido',
  INVALID_EMAIL: 'Ingrese un email válido',
  INVALID_PHONE: 'Ingrese un número de teléfono válido',
  PASSWORD_TOO_SHORT: `La contraseña debe tener al menos ${VALIDATION.PASSWORD_MIN_LENGTH} caracteres`,
  FILE_TOO_LARGE: 'El archivo es demasiado grande',
  INVALID_FILE_TYPE: 'Tipo de archivo no permitido',
} as const;

// Common success messages
export const SUCCESS_MESSAGES = {
  SAVED: 'Guardado exitosamente',
  UPDATED: 'Actualizado exitosamente',
  DELETED: 'Eliminado exitosamente',
  SENT: 'Enviado exitosamente',
  UPLOADED: 'Archivo subido exitosamente',
} as const;

// Common loading messages
export const LOADING_MESSAGES = {
  LOADING: 'Cargando...',
  SAVING: 'Guardando...',
  UPDATING: 'Actualizando...',
  DELETING: 'Eliminando...',
  UPLOADING: 'Subiendo archivo...',
  SENDING: 'Enviando...',
} as const;

// Common dialog messages
export const DIALOG_MESSAGES = {
  CONFIRM_DELETE: '¿Está seguro que desea eliminar este elemento?',
  CONFIRM_CANCEL: '¿Está seguro que desea cancelar? Los cambios no guardados se perderán.',
  UNSAVED_CHANGES: 'Tiene cambios sin guardar. ¿Desea guardarlos antes de continuar?',
} as const;

// Common storage keys
export const STORAGE_KEYS = {
  THEME: 'theme',
  LANGUAGE: 'language',
  USER_PREFERENCES: 'userPreferences',
  FORM_DATA: 'formData',
  SEARCH_HISTORY: 'searchHistory',
} as const;

// Common themes
export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
} as const;

// Common languages
export const LANGUAGES = {
  ES: 'es',
  EN: 'en',
} as const;

// Common animation durations (in milliseconds)
export const ANIMATION_DURATIONS = {
  FAST: 150,
  NORMAL: 300,
  SLOW: 500,
} as const;

// Common z-index values
export const Z_INDEX = {
  DROPDOWN: 1000,
  MODAL: 1050,
  POPOVER: 1060,
  TOOLTIP: 1070,
  TOAST: 1080,
} as const;

// Role-related constants
export const ROLES = {
  // Contextual roles that are only assigned within tramite contexts
  CONTEXTUAL_ROLES: ['vendedor', 'comprador'],
} as const;

// Filter-related constants
export const FILTERS = {
  // Default date to filter tramites from (September 1st, 2025)
  DATE_FROM_DEFAULT: '2025-09-01',
  // Delay for state synchronization when applying filters (in milliseconds)
  STATE_SYNC_DELAY: 100,
} as const;

// Tramite-specific labels
export { TRAMITE_LABELS } from './tramite-labels';